import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Formik, Form, Field, ErrorMessage, FormikProps } from 'formik';
import { useState } from 'react';
import { useGetTenantsQuery } from '@/api/tenants/getTenants';
import { useGetSquareQuery } from '@/api/squares/getSquare';
import { useSquareForm } from '@/hooks/forms/useSquareForm';
import { SquareFormValues } from '@/utils/forms/squareTransformers';
import { Title } from '@/components/composite/Title';
import { BackButton } from '@/components/composite/buttons/BackButton';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { TranslatableFieldLabel } from '@/components/composite/TranslatableFieldLabel';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TzDateTimeInput } from '@/components/datetime/TzDateTimeInput';
import { Breadcrumbs } from '@/components/layout/Breadcrumbs';
import { PageContainer } from '@/components/layout/PageContainer';
import { useFieldTranslation } from '@/hooks/useFieldTranslation';
import { PeriodsSection } from '@/components/squares/PeriodsSection';
import { TeamStylingSection } from '@/components/squares/TeamStylingSection';
import { AdsSection } from '@/components/squares/AdsSection';
import { EntriesSection } from '@/components/squares/EntriesSection';
import { ReservedSeatsSection } from '@/components/squares/ReservedSeatsSection';
import { ScoringSection } from '@/components/squares/ScoringSection';
import { FixtureSelectorInline } from '@/components/squares/FixtureSelectorInline';
import { GameRulesSection } from '@/components/squares/GameRulesSection';
import { getSportLogoUrl, getSportsForGameType } from '@low6-pickem/sports-config';
import { sportsForSelect } from '@/lib/platform-filters';

// Sports available for Squares (excludes individual sports like PGA). The option
// list itself is built in-component via sportsForSelect so the record's current
// sport stays selectable even if this client no longer deploys it (Layer A).
const SQUARES_SPORT_CODES = getSportsForGameType('squares');
import { CheckCircle2, Target, Settings, X } from 'lucide-react';
import { CircleHelp } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import * as Yup from 'yup';
import { useManualScoreSquareMutation } from '@/api/squares/manualScoreSquare';
import { GameRulesSettingsDialog } from '@/components/squares/GameRulesSettingsDialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export const Route = createFileRoute('/dashboard/squares/$id')({
  validateSearch: (search: Record<string, unknown>) => {
    return {
      tenant_id: search.tenant_id as string | undefined,
      tab: search.tab as
        | 'basic'
        | 'periods'
        | 'styling'
        | 'ads'
        | 'entries'
        | 'scoring'
        | undefined,
    };
  },
  loader: async ({ params }) => {
    if (params.id === 'new') {
      return { square: null };
    }
    // For now, return null - we'll fetch in component
    return { square: null };
  },
  component: RouteComponent,
});

// Validation schema for Squares contest
const squareValidationSchema = Yup.object({
  tenant_id: Yup.string()
    .required('Tenant is required')
    .matches(
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
      'Invalid tenant ID format'
    ),
  status: Yup.string()
    .oneOf(['DRAFT', 'PENDING', 'ACTIVE', 'COMPLETE'], 'Invalid status')
    .required('Status is required'),
  contest_name_en: Yup.string()
    .required('Contest name (English) is required')
    .min(3, 'Contest name must be at least 3 characters'),
  contest_name_fr: Yup.string().min(3, 'Contest name must be at least 3 characters'),
  sport: Yup.string().required('Sport is required'),
  start_time_utc: Yup.string().required('Start time is required'),
  entry_fee: Yup.number().min(0, 'Entry fee must be 0 or greater').required(),
  max_squares_per_user: Yup.number().min(1, 'Must allow at least 1 square per user').required(),
  grid_rows: Yup.number().min(1).max(20).required(),
  grid_cols: Yup.number().min(1).max(20).required(),
  fixture_id: Yup.string().optional(),
  periods: Yup.array()
    .min(1, 'At least one period is required')
    .test(
      'period-prizes-sum',
      'Sum of period prizes must equal total prize amount',
      function (periods) {
        if (!periods || periods.length === 0) return true;
        // Calculate sum of period prizes
        const sum = periods.reduce((acc: number, p: any) => {
          const prize =
            typeof p.prizeAmount === 'number'
              ? p.prizeAmount
              : parseFloat(p.prizeAmount?.toString() || '0');
          return acc + (isNaN(prize) ? 0 : prize);
        }, 0);
        // Since total_prize_amount is auto-calculated from periods, it should always match
        // But we validate that all period prizes are valid numbers
        return sum >= 0; // Allow any non-negative sum (total will auto-update)
      }
    ),
  total_prize_amount: Yup.number().min(0, 'Total prize must be 0 or greater'),
});

export function RouteComponent() {
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const isNewSquare = id === 'new';
  const [activeTab, setActiveTab] = useState<string>(search.tab || 'basic');
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);

  const {
    data: tenants = [],
    isLoading: tenantsLoading,
    error: tenantsError,
  } = useGetTenantsQuery();
  const { data: squareData, isLoading: squareLoading } = useGetSquareQuery(
    isNewSquare ? undefined : id
  );
  const square = squareData?.squares_contest;

  // Use search.tenant_id only for initial form - never tenants[0], to avoid form reset when
  // tenants load (enableReinitialize would reset all fields including sport selection)
  const stableSearchLeagueId = search.tenant_id || undefined;

  const form = useSquareForm({
    square: square || null,
    searchLeagueId: stableSearchLeagueId,
    isNewSquare,
  });

  const { translateField, isFieldTranslating } = useFieldTranslation();
  const { mutate: manualScoreMutate, isPending: manualScorePending } =
    useManualScoreSquareMutation();

  const isSquareEditable = square?.editable !== false;
  // A contest that has started (but is not finalized/cancelled) is locked for most fields,
  // but cosmetic styling/asset/ad fields can still be updated. The backend enforces this.
  const isFinal = square?.status === 'final';
  const isCancelled = square?.status === 'cancelled';
  const isStylingEditable = isSquareEditable || (!isFinal && !isCancelled);

  if (squareLoading && !isNewSquare) {
    return (
      <PageContainer>
        <Card>
          <CardContent>
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="text-muted-foreground">Loading squares contest...</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  // Use form hook's initial values - tenant_id comes from form (search.tenant_id) or will be
  // set via setFieldValue when tenants load; avoid using tenants[0] here to prevent
  // enableReinitialize from resetting form (and losing sport selection) when tenants load
  const initialValues: SquareFormValues = {
    tenant_id: form.initialValues.tenant_id || search.tenant_id || '',
    contest_name_en: form.initialValues.contest_name_en || '',
    contest_name_fr: form.initialValues.contest_name_fr || '',
    contest_subtext_en: form.initialValues.contest_subtext_en || '',
    contest_subtext_fr: form.initialValues.contest_subtext_fr || '',
    lobby_tag_input: form.initialValues.lobby_tag_input || '',
    lobby_tags: form.initialValues.lobby_tags || [],
    status: form.initialValues.status || 'DRAFT',
    entry_fee: form.initialValues.entry_fee ?? 0,
    max_squares_per_user: form.initialValues.max_squares_per_user ?? 1,
    sport: form.initialValues.sport || 'NCAAMB',
    start_time_utc: form.initialValues.start_time_utc || '',
    open_time_utc: form.initialValues.open_time_utc || '',
    selection_editable_until_utc: form.initialValues.selection_editable_until_utc || '',
    reserve_a_seat_enabled: form.initialValues.reserve_a_seat_enabled ?? false,
    questions_posted_at: form.initialValues.questions_posted_at || '',
    row_team_id: form.initialValues.row_team_id || '',
    col_team_id: form.initialValues.col_team_id || '',
    row_team_display_name: form.initialValues.row_team_display_name || '',
    col_team_display_name: form.initialValues.col_team_display_name || '',
    row_team_abbreviation: form.initialValues.row_team_abbreviation || '',
    col_team_abbreviation: form.initialValues.col_team_abbreviation || '',
    row_team_bg_color: form.initialValues.row_team_bg_color || '',
    col_team_bg_color: form.initialValues.col_team_bg_color || '',
    row_team_icon_url: form.initialValues.row_team_icon_url || '',
    col_team_icon_url: form.initialValues.col_team_icon_url || '',
    auto_team_assets_from_matchup: form.initialValues.auto_team_assets_from_matchup ?? true,
    fixture_id: form.initialValues.fixture_id || '',
    scheduled_game_start_utc: form.initialValues.scheduled_game_start_utc || '',
    feed_provider: form.initialValues.feed_provider || '',
    grid_rows: form.initialValues.grid_rows ?? 10,
    grid_cols: form.initialValues.grid_cols ?? 10,
    periods: form.initialValues.periods || [],
    winner_rule: form.initialValues.winner_rule || 'last_digit_of_score',
    winner_rule_config: form.initialValues.winner_rule_config || {},
    payout_distribution_method:
      form.initialValues.payout_distribution_method || 'split_among_winners',
    total_prize_amount: form.initialValues.total_prize_amount ?? 0,
    prize_text_en: form.initialValues.prize_text_en || '',
    prize_text_fr: form.initialValues.prize_text_fr || '',
    gam_enabled: form.initialValues.gam_enabled !== false,
    google_manager_sdk_enabled: form.initialValues.google_manager_sdk_enabled !== false,
    ad_units: form.initialValues.ad_units || [],
    sticky_footer_placement: form.initialValues.sticky_footer_placement ?? null,
    badge_image_url: form.initialValues.badge_image_url || '',
    background_image_url: form.initialValues.background_image_url || '',
    rules_image_url: form.initialValues.rules_image_url || '',
    submit_view_image_url: form.initialValues.submit_view_image_url || '',
    game_rules_version_id: form.initialValues.game_rules_version_id || '',
  };

  const handleSubmit = async (values: SquareFormValues, { setSubmitting }: any) => {
    try {
      await form.handleSubmit(values, { setSubmitting });
    } catch (error) {
      console.error('Failed to save squares contest:', error);
    }
  };

  const normalizeLobbyTag = (value: string) =>
    value
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-_]/g, '');

  return (
    <PageContainer>
      <Breadcrumbs
        overrides={{ squares: 'Squares' }}
        entityName={square?.contest_name_en || (id === 'new' ? 'New Contest' : 'Edit')}
      />
      <Card className="flex flex-col gap-4 w-full max-w-4xl mx-auto">
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <Title>{square ? 'Edit' : 'Add new'} Squares Contest</Title>
            <BackButton />
          </div>
        </CardHeader>

        <CardContent>
          <Formik
            initialValues={initialValues}
            validationSchema={squareValidationSchema}
            onSubmit={handleSubmit}
            enableReinitialize={!isNewSquare}
            validateOnChange={false}
            validateOnBlur={true}
            key={square?.squares_contest_id || 'new'}
          >
            {({
              isSubmitting,
              errors,
              touched,
              values,
              setFieldValue,
              setFieldTouched,
            }: FormikProps<SquareFormValues>) => {
              const addLobbyTag = (rawValue: string) => {
                const normalized = normalizeLobbyTag(rawValue);
                if (!normalized) return;
                const existing = values.lobby_tags || [];
                if (existing.includes(normalized)) return;
                setFieldValue('lobby_tags', [...existing, normalized]);
              };

              const removeLobbyTag = (tag: string) => {
                setFieldValue(
                  'lobby_tags',
                  (values.lobby_tags || []).filter(existingTag => existingTag !== tag)
                );
              };

              // Auto-set tenant_id to first tenant if not set
              if (!values.tenant_id && tenants.length > 0) {
                setFieldValue('tenant_id', tenants[0].tenant_id, false);
              }

              // Create fixture select handler with access to form values
              const handleFixtureSelectWithForm = (fixtureId: string, fixture: any) => {
                setFieldValue('fixture_id', fixtureId);
                setFieldValue('scheduled_game_start_utc', fixture.scheduledDate);
                setFieldValue('feed_provider', 'feed-service');

                // Auto-set Start Date & Time to actual fixture game time
                if (!values.start_time_utc) {
                  setFieldValue('start_time_utc', fixture.scheduledDate);
                }

                // Auto-populate teams with IDs and names
                if (fixture.homeTeamId) {
                  setFieldValue('row_team_id', fixture.homeTeamId);
                  setFieldValue(
                    'row_team_display_name',
                    fixture.homeTeamName || fixture.homeTeamId
                  );
                  setFieldValue('row_team_abbreviation', fixture.homeTeamAbbreviation || '');
                }
                if (fixture.awayTeamId) {
                  setFieldValue('col_team_id', fixture.awayTeamId);
                  setFieldValue(
                    'col_team_display_name',
                    fixture.awayTeamName || fixture.awayTeamId
                  );
                  setFieldValue('col_team_abbreviation', fixture.awayTeamAbbreviation || '');
                }
              };
              return (
                <Form className="flex flex-col gap-6">
                  {/* Settings Icon */}
                  <div className="flex justify-end mb-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setSettingsDialogOpen(true)}
                      className="gap-2"
                    >
                      <Settings className="h-4 w-4" />
                      Game Rules Settings
                    </Button>
                  </div>

                  <Tabs
                    value={activeTab}
                    onValueChange={value => {
                      setActiveTab(value);
                      navigate({
                        to: '/dashboard/squares/$id',
                        params: { id },
                        search: { ...search, tab: value as any },
                        replace: true,
                      });
                    }}
                    className="w-full"
                  >
                    <TabsList className="grid w-full grid-cols-7">
                      <TabsTrigger value="basic">Basic</TabsTrigger>
                      <TabsTrigger value="periods">Periods</TabsTrigger>
                      <TabsTrigger value="styling">Styling</TabsTrigger>
                      <TabsTrigger value="ads">Google Ads</TabsTrigger>
                      <TabsTrigger value="entries">Entries</TabsTrigger>
                      <TabsTrigger value="reserved-seats">Reserved Seats</TabsTrigger>
                      <TabsTrigger value="scoring">Scoring</TabsTrigger>
                    </TabsList>

                    <TabsContent value="basic" className="space-y-4">
                      <h2 className="text-lg font-semibold">Basic Information</h2>

                      {/* Tenant Selection */}
                      <div className="flex flex-col gap-2">
                        <Label htmlFor="tenant_id">Tenant *</Label>
                        {tenantsLoading ? (
                          <div className="text-sm text-muted-foreground">Loading tenants...</div>
                        ) : tenantsError ? (
                          <div className="text-sm text-red-500">
                            Error loading tenants:{' '}
                            {tenantsError instanceof Error ? tenantsError.message : 'Unknown error'}
                            <br />
                            <span className="text-xs">
                              Please check your authentication and try refreshing the page.
                            </span>
                          </div>
                        ) : tenants.length === 0 ? (
                          <div className="text-sm text-red-500">
                            No tenants available. Please ensure tenants are configured.
                          </div>
                        ) : (
                          <Select
                            value={values.tenant_id || ''}
                            onValueChange={value => {
                              setFieldValue('tenant_id', value);
                              setFieldTouched('tenant_id', true);
                            }}
                            disabled={!isSquareEditable || tenantsLoading}
                          >
                            <SelectTrigger
                              data-testid="square-tenant_id-select"
                              className={
                                errors.tenant_id && touched.tenant_id ? 'border-red-500' : ''
                              }
                            >
                              <SelectValue placeholder="Select a tenant" />
                            </SelectTrigger>
                            <SelectContent>
                              {tenants.map(tenant => (
                                <SelectItem key={tenant.tenant_id} value={tenant.tenant_id}>
                                  {tenant.name_en || tenant.title || `Tenant #${tenant.tenant_id}`}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        <ErrorMessage name="tenant_id">
                          {msg => <span className="text-sm text-red-500">{msg}</span>}
                        </ErrorMessage>
                        {tenants.length > 0 && !values.tenant_id && (
                          <div className="text-xs text-muted-foreground">
                            {tenants.length} tenant{tenants.length !== 1 ? 's' : ''} available
                          </div>
                        )}
                      </div>

                      {/* Sport Selection */}
                      <div className="flex flex-col gap-2">
                        <Label htmlFor="sport">Sport *</Label>
                        <Select
                          value={values.sport || 'NCAAMB'}
                          onValueChange={value => {
                            setFieldValue('sport', value);
                            setFieldTouched('sport', true);
                          }}
                          disabled={!isSquareEditable}
                        >
                          <SelectTrigger
                            data-testid="square-sport-select"
                            className={errors.sport && touched.sport ? 'border-red-500' : ''}
                          >
                            <SelectValue placeholder="Select sport" />
                          </SelectTrigger>
                          <SelectContent>
                            {sportsForSelect(values.sport)
                              .filter(
                                league =>
                                  SQUARES_SPORT_CODES.includes(league.code as any) ||
                                  league.code === values.sport
                              )
                              .map(league => (
                                <SelectItem key={league.code} value={league.code}>
                                  <div className="flex items-center gap-2">
                                    <img
                                      src={getSportLogoUrl(league.code)}
                                      alt={league.code}
                                      className="w-5 h-5 object-contain"
                                    />
                                    <span>{league.filterLabel || league.name}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            <SelectItem value="NHL">
                              <div className="flex items-center gap-2">
                                <img
                                  src={getSportLogoUrl('NHL')}
                                  alt="NHL"
                                  className="w-5 h-5 object-contain"
                                />
                                <span>NHL</span>
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <ErrorMessage name="sport">
                          {msg => <span className="text-sm text-red-500">{msg}</span>}
                        </ErrorMessage>
                      </div>

                      {/* Contest Name (English) */}
                      <div className="flex flex-col gap-2">
                        <TranslatableFieldLabel
                          htmlFor="contest_name_en"
                          onTranslate={() =>
                            translateField(
                              'contest_name_en',
                              values.contest_name_en,
                              'contest_name_fr',
                              setFieldValue
                            )
                          }
                          isTranslating={isFieldTranslating('contest_name_en')}
                        >
                          Contest Name (English) *
                        </TranslatableFieldLabel>
                        <Field
                          as={Input}
                          id="contest_name_en"
                          name="contest_name_en"
                          data-testid="square-contest_name_en"
                          placeholder="Enter contest name in English"
                          readOnly={!isSquareEditable}
                          disabled={!isSquareEditable}
                          className={`${errors.contest_name_en && touched.contest_name_en ? 'border-red-500' : ''} ${!isSquareEditable ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                        />
                        <ErrorMessage name="contest_name_en">
                          {msg => <span className="text-sm text-red-500">{msg}</span>}
                        </ErrorMessage>
                      </div>

                      {/* Contest Name (Spanish) */}
                      <div className="flex flex-col gap-2">
                        <Label htmlFor="contest_name_fr">Contest Name (Spanish)</Label>
                        <Field
                          as={Input}
                          id="contest_name_fr"
                          name="contest_name_fr"
                          data-testid="square-contest_name_fr"
                          placeholder="Enter contest name in Spanish"
                          readOnly={isFieldTranslating('contest_name_en') || !isSquareEditable}
                          disabled={!isSquareEditable}
                          className={`${errors.contest_name_fr && touched.contest_name_fr ? 'border-red-500' : ''} ${!isSquareEditable ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                        />
                        <ErrorMessage name="contest_name_fr">
                          {msg => <span className="text-sm text-red-500">{msg}</span>}
                        </ErrorMessage>
                      </div>

                      {/* Fixture Selection - Auto-populates teams and game time */}
                      {values.sport && (
                        <FixtureSelectorInline
                          sport={values.sport}
                          selectedFixtureId={values.fixture_id}
                          onSelect={handleFixtureSelectWithForm}
                          disabled={!isSquareEditable}
                          errors={errors}
                          touched={touched}
                          // Seed the date filter from the saved fixture date so
                          // the previously selected fixture stays visible on edit.
                          initialScheduledDate={values.scheduled_game_start_utc}
                        />
                      )}

                      {/* League IP toggle */}
                      <div className="flex items-center gap-2">
                        <Switch
                          id="league_ip"
                          checked={values.league_ip ?? false}
                          onCheckedChange={checked => setFieldValue('league_ip', checked)}
                          disabled={!isSquareEditable}
                        />
                        <div className="flex items-center gap-2">
                          <Label htmlFor="league_ip" className="cursor-pointer">
                            League IP
                          </Label>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  className="text-muted-foreground hover:text-foreground"
                                  aria-label="League IP help"
                                >
                                  <CircleHelp className="h-4 w-4" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-sm">
                                When on, team icons use licensed team logos; when off, generic team
                                avatars. Off by default. Changing this only affects icons derived
                                from now on — re-select the fixture (with auto-load on) to refresh
                                existing icons.
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </div>

                      {/* Hide from Lobby toggle */}
                      <div className="flex items-center gap-2">
                        <Switch
                          id="lobby_hidden"
                          checked={values.lobby_hidden ?? false}
                          onCheckedChange={checked => setFieldValue('lobby_hidden', checked)}
                          disabled={!isSquareEditable}
                        />
                        <div className="flex items-center gap-2">
                          <Label htmlFor="lobby_hidden" className="cursor-pointer">
                            Hide from Lobby
                          </Label>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  className="text-muted-foreground hover:text-foreground"
                                  aria-label="Hide from Lobby help"
                                >
                                  <CircleHelp className="h-4 w-4" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-sm">
                                When on, this contest won't appear in the Lobby (sections, search or
                                featured rows) — players can only enter via its direct URL. Players
                                who have joined still see it in My Games. Off by default.
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </div>

                      {/* Reserve a Seat toggle (DKPLAT-529) */}
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <Switch
                            id="reserve_a_seat_enabled"
                            data-testid="square-reserve_a_seat_enabled"
                            checked={values.reserve_a_seat_enabled ?? false}
                            onCheckedChange={checked =>
                              setFieldValue('reserve_a_seat_enabled', checked)
                            }
                            disabled={!isSquareEditable}
                          />
                          <Label htmlFor="reserve_a_seat_enabled" className="cursor-pointer">
                            Reserve a Seat
                          </Label>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  className="text-muted-foreground hover:text-foreground"
                                  aria-label="Reserve a Seat help"
                                >
                                  <CircleHelp className="h-4 w-4" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-sm">
                                When on, users can reserve a seat from the Open Date &amp; Time
                                until the &ldquo;Questions Posted At&rdquo; time, when the squares
                                grid opens for picks. Requires a future &ldquo;Questions Posted
                                At&rdquo; time. Off by default — falls back to the normal play flow.
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        {/* Questions Posted At (DKPLAT-529) — reserve window end on the linked pickem */}
                        {values.reserve_a_seat_enabled === true && (
                          <div className="grid grid-cols-2 gap-4">
                            <div className="flex flex-col gap-2">
                              <TzDateTimeInput
                                label="Questions Posted At"
                                id="questions_posted_at"
                                data-testid="square-questions_posted_at"
                                value={values.questions_posted_at || ''}
                                onChange={value =>
                                  setFieldValue('questions_posted_at', value || '')
                                }
                                disabled={!isSquareEditable}
                                description="Users can reserve a seat from the Open Date & Time until this time, when the squares grid becomes available for picks — set it after Open and before Start. Leave empty to disable the reserve window."
                                className={!isSquareEditable ? 'bg-gray-50 cursor-not-allowed' : ''}
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Lobby Tags */}
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <Label htmlFor="lobby_tags">Lobby Tags (optional)</Label>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  className="text-muted-foreground hover:text-foreground"
                                  aria-label="Lobby tags help"
                                >
                                  <CircleHelp className="h-4 w-4" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>
                                Comma-separated tags used by lobby section filters (e.g. popular,
                                nba, featured).
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <div className="flex gap-2">
                          <Input
                            id="lobby_tags"
                            data-testid="square-lobby_tags"
                            value={values.lobby_tag_input || ''}
                            onChange={event => setFieldValue('lobby_tag_input', event.target.value)}
                            onKeyDown={event => {
                              if (event.key === 'Enter' || event.key === ',') {
                                event.preventDefault();
                                addLobbyTag(values.lobby_tag_input || '');
                                setFieldValue('lobby_tag_input', '');
                              }
                            }}
                            placeholder="Type tag and press Enter"
                            disabled={!isSquareEditable}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            disabled={!isSquareEditable}
                            onClick={() => {
                              addLobbyTag(values.lobby_tag_input || '');
                              setFieldValue('lobby_tag_input', '');
                            }}
                          >
                            Add Tag
                          </Button>
                        </div>
                        {(values.lobby_tags || []).length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {(values.lobby_tags || []).map(tag => (
                              <span
                                key={tag}
                                className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-1 text-xs"
                              >
                                {tag}
                                <button
                                  type="button"
                                  className="rounded-sm hover:bg-black/10 p-0.5"
                                  aria-label={`Remove ${tag} tag`}
                                  onClick={() => removeLobbyTag(tag)}
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                        <p className="text-xs text-muted-foreground">
                          Leave empty for no tags. Tags help place contests into dynamic lobby rows.
                        </p>
                      </div>

                      {/* Manual Team Names Entry */}
                      <div className="space-y-4 border-t pt-6">
                        <div>
                          <h3 className="text-lg font-semibold mb-2">Team Names</h3>
                          <p className="text-sm text-muted-foreground mb-4">
                            Manually enter team names, or select a fixture above to auto-populate.
                          </p>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="flex flex-col gap-2">
                            <Label htmlFor="row_team_display_name">Team 1 (Row Team) Name</Label>
                            <Field
                              as={Input}
                              id="row_team_display_name"
                              name="row_team_display_name"
                              placeholder="e.g., Los Angeles Lakers"
                              disabled={!isSquareEditable}
                              className={
                                errors.row_team_display_name && touched.row_team_display_name
                                  ? 'border-red-500'
                                  : ''
                              }
                            />
                            <p className="text-xs text-muted-foreground">
                              Name of the team that will appear on the row axis
                            </p>
                          </div>

                          <div className="flex flex-col gap-2">
                            <Label htmlFor="col_team_display_name">Team 2 (Column Team) Name</Label>
                            <Field
                              as={Input}
                              id="col_team_display_name"
                              name="col_team_display_name"
                              placeholder="e.g., Atlanta Hawks"
                              disabled={!isSquareEditable}
                              className={
                                errors.col_team_display_name && touched.col_team_display_name
                                  ? 'border-red-500'
                                  : ''
                              }
                            />
                            <p className="text-xs text-muted-foreground">
                              Name of the team that will appear on the column axis
                            </p>
                          </div>
                        </div>

                        {/* Show if teams are set from fixture */}
                        {values.fixture_id &&
                          (values.row_team_display_name || values.col_team_display_name) && (
                            <div className="border rounded-lg p-3 bg-primary/5 border-primary/20">
                              <div className="flex items-center gap-2 mb-1">
                                <CheckCircle2 className="h-3 w-3 text-primary" />
                                <span className="text-xs font-medium text-primary">
                                  Teams can be edited manually or will be updated when fixture
                                  changes
                                </span>
                              </div>
                            </div>
                          )}
                      </div>

                      {/* Prize Text - Like Pickem
                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex flex-col gap-2">
                          <TranslatableFieldLabel
                            htmlFor="prize_text_en"
                            onTranslate={() =>
                              translateField(
                                'prize_text_en',
                                values.prize_text_en || '',
                                'prize_text_fr',
                                setFieldValue
                              )
                            }
                            isTranslating={isFieldTranslating('prize_text_en')}
                          >
                            Prize Text (English)
                          </TranslatableFieldLabel>
                          <Field
                            as={Input}
                            id="prize_text_en"
                            name="prize_text_en"
                            placeholder="e.g., 1st $250 Bet Credits"
                            disabled={!isSquareEditable}
                          />
                        </div>

                        <div className="flex flex-col gap-2">
                          <Label htmlFor="prize_text_fr">Prize Text (Spanish)</Label>
                          <Field
                            as={Input}
                            id="prize_text_fr"
                            name="prize_text_fr"
                            placeholder="e.g., 1er 250 $ en crédits de paris"
                            disabled={!isSquareEditable}
                          />
                        </div>
                      </div>
                      */}

                      {/* Dates Section - Like Pickem */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex flex-col gap-2">
                          <TzDateTimeInput
                            label="Open Date & Time"
                            id="selection_editable_until_utc"
                            data-testid="square-selection_editable_until_utc"
                            value={values.selection_editable_until_utc || ''}
                            onChange={value => setFieldValue('selection_editable_until_utc', value)}
                            disabled={!isSquareEditable}
                            className={`${errors.selection_editable_until_utc && touched.selection_editable_until_utc ? 'border-red-500' : ''} ${!isSquareEditable ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                          />
                          <p className="text-xs text-muted-foreground">
                            Contest is visible to users from this date/time until Start Date & Time.
                            Set this to control when the contest appears on the Squares app.
                          </p>
                          <ErrorMessage name="selection_editable_until_utc">
                            {msg => <span className="text-sm text-red-500">{msg}</span>}
                          </ErrorMessage>
                        </div>

                        <div className="flex flex-col gap-2">
                          <TzDateTimeInput
                            label="Start Date & Time"
                            id="start_time_utc"
                            data-testid="square-start_time_utc"
                            value={values.start_time_utc}
                            onChange={value => setFieldValue('start_time_utc', value)}
                            disabled={!isSquareEditable}
                            required
                            className={`${errors.start_time_utc && touched.start_time_utc ? 'border-red-500' : ''} ${!isSquareEditable ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                          />
                          <p className="text-xs text-muted-foreground">
                            After this date/time users will no longer be able to make selections and
                            numbers are revealed.
                          </p>
                          <ErrorMessage name="start_time_utc">
                            {msg => <span className="text-sm text-red-500">{msg}</span>}
                          </ErrorMessage>
                        </div>
                      </div>

                      {/* Status Field */}
                      <div className="flex flex-col gap-2">
                        <Label htmlFor="status">Status *</Label>
                        <Select
                          value={values.status || 'DRAFT'}
                          onValueChange={value => {
                            setFieldValue('status', value);
                            setFieldTouched('status', true);
                          }}
                          disabled={!isSquareEditable}
                        >
                          <SelectTrigger
                            data-testid="square-status-select"
                            className={errors.status && touched.status ? 'border-red-500' : ''}
                          >
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="DRAFT">Draft</SelectItem>
                            <SelectItem value="PENDING">Pending</SelectItem>
                            <SelectItem value="ACTIVE">Active</SelectItem>
                            <SelectItem value="COMPLETE">Complete</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          Draft: Initial state. Pending: Ready for activation. Active: Visible and
                          accepting entries (use this for entries). Complete: Contest finished.
                        </p>
                        <ErrorMessage name="status">
                          {msg => <span className="text-sm text-red-500">{msg}</span>}
                        </ErrorMessage>
                      </div>

                      {/* Game Rules Section */}
                      <GameRulesSection disabled={!isSquareEditable} />
                    </TabsContent>

                    <TabsContent value="periods" className="space-y-4">
                      <PeriodsSection disabled={!isSquareEditable} />
                    </TabsContent>

                    <TabsContent value="styling" className="space-y-4">
                      {/*
                        Tenant code is needed for tenant image library selection in custom styling assets.
                      */}
                      <TeamStylingSection
                        disabled={!isStylingEditable}
                        contestId={square?.squares_contest_id || square?.question_id || id}
                        tenantCode={tenants.find(t => t.tenant_id === values.tenant_id)?.code}
                      />
                    </TabsContent>

                    <TabsContent value="ads" className="space-y-4">
                      <AdsSection disabled={!isStylingEditable} tenantId={values.tenant_id} />
                    </TabsContent>

                    <TabsContent value="entries" className="space-y-4">
                      <EntriesSection
                        disabled={!isSquareEditable}
                        squareId={square?.question_id || square?.squares_contest_id || id || null}
                      />
                    </TabsContent>

                    <TabsContent value="reserved-seats" className="space-y-4">
                      <ReservedSeatsSection
                        squareId={square?.question_id || square?.squares_contest_id || id || null}
                      />
                    </TabsContent>

                    <TabsContent value="scoring" className="space-y-4">
                      <ScoringSection
                        disabled={false}
                        squareId={square?.squares_contest_id || square?.question_id || id}
                      />
                    </TabsContent>
                  </Tabs>

                  {/* Action Buttons */}
                  <div className="flex justify-between items-center gap-2 pt-4 border-t">
                    {/* Manual Refresh Button (only show for existing contests with fixture) */}
                    {square && square.fixture_id && (
                      <Button
                        type="button"
                        variant="default"
                        onClick={() => {
                          if (square.squares_contest_id) {
                            manualScoreMutate(square.squares_contest_id);
                          } else if (square.question_id) {
                            manualScoreMutate(square.question_id);
                          } else {
                            manualScoreMutate(id);
                          }
                        }}
                        disabled={manualScorePending || !square.fixture_id}
                        className="gap-2"
                      >
                        <Target className="w-4 h-4" />
                        {manualScorePending ? 'Refreshing...' : 'Manual Refresh'}
                      </Button>
                    )}
                    {!square?.fixture_id && (
                      <div className="text-sm text-muted-foreground">
                        Select a fixture to enable manual scoring
                      </div>
                    )}

                    {/* Submit Button */}
                    <div className="flex gap-2 ml-auto">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => navigate({ to: '/dashboard/squares' })}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={isSubmitting || form.isPending || !isStylingEditable}
                      >
                        {isSubmitting || form.isPending
                          ? 'Saving...'
                          : square
                            ? 'Update Squares Contest'
                            : 'Create Squares Contest'}
                      </Button>
                    </div>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </CardContent>
      </Card>

      {/* Game Rules Settings Dialog */}
      <GameRulesSettingsDialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen} />
    </PageContainer>
  );
}
